// useTranslatedContent — the React hook UI components use to render any
// auto-translated UGC. Works for messages, reviews, comments, posts,
// product descriptions — whatever entity_type is registered in
// core.fn_can_read_entity / core.fn_resolve_entity_source.
//
// Behavior:
//   1. If viewer locale === source locale, render source (no translation
//      needed). Hook reports `isTranslated: false`.
//   2. If a content_translations row exists for (entity, viewer locale),
//      render it. Hook reports `isTranslated: true`, exposes `display`
//      as the translation and `source` as the original; `toggleOriginal`
//      flips which is rendered.
//   3. If no row exists, hook calls rpc_request_translation to enqueue
//      one, reports `isInFlight: true`, and shows source meanwhile. The
//      realtime subscription on core.content_translations delivers the
//      result, at which point the hook re-renders with the translation.
//
// The hook is per-message — each card / message / row has its own state.
// `showingOriginal` is component-local; it does NOT mutate the DB.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getContentTranslation,
  requestContentTranslation,
  subscribeContentTranslation,
  type ContentTranslation,
} from '@pc/db';

import { normalizeLocale } from './locales';

// How long the hook will show "translating" before giving up and falling
// back to source text. Covers worker outages, circuit-breaker-tripped
// states (rpc_request_translation returns null silently), and the
// content_translations realtime channel never firing because the dispatch
// row went to status=error. UX rule: never spin forever — under any
// failure mode the user sees the original message within this window.
const INFLIGHT_TIMEOUT_MS = 45_000;

export interface UseTranslatedContentArgs {
  entityType: string;
  entityId: string;
  fieldName?: string;
  /** The original text (source-language). Required so we can render it as fallback. */
  sourceText: string;
  /** The detected/declared source locale of `sourceText`. null = unknown. */
  sourceLocale?: string | null;
  /**
   * Override viewer locale. Defaults to the active react-i18next language.
   */
  viewerLocaleOverride?: string;
  /**
   * Disable the hook entirely (e.g. for messages the viewer themselves
   * sent — translating your own outbound message is wasteful).
   */
  disabled?: boolean;
}

export interface UseTranslatedContentResult {
  /** Whatever the UI should render right now. */
  display: string;
  source: string;
  translated: string | null;
  isTranslated: boolean;
  isInFlight: boolean;
  showingOriginal: boolean;
  sourceLocale: string | null;
  viewerLocale: string;
  toggleOriginal: () => void;
}

export function useTranslatedContent(
  args: UseTranslatedContentArgs,
): UseTranslatedContentResult {
  const { i18n } = useTranslation();
  const viewerLocale = normalizeLocale(args.viewerLocaleOverride ?? i18n.language);
  const sourceLocale = args.sourceLocale ? normalizeLocale(args.sourceLocale) : null;
  const fieldName = args.fieldName ?? 'body';

  const sameLanguage = sourceLocale != null && sourceLocale === viewerLocale;
  const skip = args.disabled || sameLanguage;

  const [translated, setTranslated] = useState<string | null>(null);
  const [inFlight, setInFlight] = useState<boolean>(false);
  const [showingOriginal, setShowingOriginal] = useState<boolean>(false);

  const toggleOriginal = useCallback(() => {
    setShowingOriginal((v) => !v);
  }, []);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearInflightTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setTranslated(null);
    setInFlight(false);
    setShowingOriginal(false);
    clearInflightTimer();

    if (skip) return () => {};

    let unsubscribe: (() => void) | undefined;

    (async () => {
      try {
        const existing = await getContentTranslation({
          entityType: args.entityType,
          entityId: args.entityId,
          fieldName,
          targetLocale: viewerLocale,
        });
        if (cancelled) return;
        if (existing) {
          setTranslated(existing.content);
          setInFlight(false);
          return;
        }

        // No row yet. Subscribe first so we don't miss the INSERT race,
        // then enqueue the request.
        unsubscribe = subscribeContentTranslation(
          {
            entityType: args.entityType,
            entityId: args.entityId,
            fieldName,
          },
          (row: ContentTranslation) => {
            if (row.target_locale === viewerLocale) {
              setTranslated(row.content);
              setInFlight(false);
              clearInflightTimer();
            }
          },
        );

        setInFlight(true);
        // Hard cap on the spinner. If nothing arrives in INFLIGHT_TIMEOUT_MS
        // we silently fall back to source. Covers: dispatch row went to
        // status=error (no INSERT ever fires), worker outage, dispatch
        // never claimed, etc.
        timeoutRef.current = setTimeout(() => {
          if (cancelled) return;
          setInFlight(false);
        }, INFLIGHT_TIMEOUT_MS);

        try {
          const dispatchId = await requestContentTranslation({
            entityType: args.entityType,
            entityId: args.entityId,
            fieldName,
            targetLocale: viewerLocale,
          });
          // rpc_request_translation returns null when:
          //   - translation already exists (the getContentTranslation
          //     race above missed it; the realtime sub will deliver it
          //     if it INSERTs again, but typically it already exists and
          //     we just won't get one)
          //   - the platform is disabled / circuit breaker tripped
          //   - source locale equals target locale
          // In all those cases, stop showing the spinner immediately.
          if (cancelled) return;
          if (dispatchId == null) {
            setInFlight(false);
            clearInflightTimer();
          }
        } catch (err) {
          if (!cancelled) {
            setInFlight(false);
            clearInflightTimer();
          }
          // eslint-disable-next-line no-console
          console.warn('[useTranslatedContent] requestContentTranslation failed:', err);
        }
      } catch (err) {
        if (!cancelled) {
          setInFlight(false);
          clearInflightTimer();
        }
        // eslint-disable-next-line no-console
        console.warn('[useTranslatedContent] getContentTranslation failed:', err);
      }
    })();

    return () => {
      cancelled = true;
      clearInflightTimer();
      if (unsubscribe) unsubscribe();
    };
  }, [
    args.entityType,
    args.entityId,
    fieldName,
    viewerLocale,
    skip,
    clearInflightTimer,
  ]);

  const isTranslated = translated != null && !sameLanguage;
  const display = useMemo(() => {
    if (showingOriginal) return args.sourceText;
    if (isTranslated && translated) return translated;
    return args.sourceText;
  }, [showingOriginal, isTranslated, translated, args.sourceText]);

  return {
    display,
    source: args.sourceText,
    translated,
    isTranslated,
    isInFlight: inFlight && !isTranslated,
    showingOriginal,
    sourceLocale,
    viewerLocale,
    toggleOriginal,
  };
}
